import toLower from "lodash/toLower"
import isBoolean from "lodash/isBoolean"
import split from "lodash/split"
import trim from "lodash/trim"
import React, { Component } from "react"
import PropTypes from "prop-types"
import { isHomePage } from "utils-lib/route"
import { wmColor } from "themes-lib/wmTheme"
import { getWindow } from "utils-lib/getWindow"
import "../styles/spacing.scss"

const ssrWindow = getWindow()

const SHOULD_SHOW_BOTTOM_SPACING_BY_DEFAULT = true
const aemAuthoringPrefix = `editor.html/content/wm/`

const BACKGROUND_COLOR_OPTIONS = {
  white: `white`,
  beige: `beige`,
  sand: `sand`,
  green: `green`,
  green1: `green1`,
  yellow: `yellow`,
}

/**
 * Matches a given color value to a corresponding wmColor value.
 * @param {string} val - The color value to match.
 * @returns {string} - The corresponding wmColor value.
 */
const matchColors = (val) => {
  if (val === BACKGROUND_COLOR_OPTIONS.sand) {
    return wmColor.sand
  }
  if (val === BACKGROUND_COLOR_OPTIONS.beige) {
    return wmColor.beige
  }
  if (
    val === BACKGROUND_COLOR_OPTIONS.green1 ||
    val === BACKGROUND_COLOR_OPTIONS.green
  ) {
    return wmColor.green1
  }
  if (val === BACKGROUND_COLOR_OPTIONS.white) {
    return wmColor.white
  }
  if (val === BACKGROUND_COLOR_OPTIONS.yellow) {
    return wmColor.primaryYellow
  }
  return val
}

/**
 * Determines if the current page is an L1 page.
 * @returns {boolean} A boolean value indicating if the current page is an L1 page.
 */
export const isL1Page = () => {
  const path = trim(ssrWindow.location.pathname, `/`).replace(
    aemAuthoringPrefix,
    ``,
  )
  return split(path, `/`).length === 3
}

/**
 * Determines if the current page is an L2 page.
 * @returns {boolean} A boolean value indicating if the current page is an L2 page.
 */
export const isL2Page = () => {
  const path = trim(ssrWindow.location.pathname, `/`).replace(
    aemAuthoringPrefix,
    ``,
  )
  return split(path, `/`).length === 4
}

/**
 * Determines if the current page is an L3 page.
 * @returns {boolean} A boolean value indicating if the current page is an L3 page.
 */
export const isL3Page = () => {
  const path = trim(ssrWindow.location.pathname, `/`).replace(
    aemAuthoringPrefix,
    ``,
  )
  return split(path, `/`).length === 5
}

const isTrue = (value) => toLower(value) === `true` || value === true
const isFalse = (value) =>
  toLower(value) === `false` || value === false || value === ""

/**
 * Determines if the component should show top spacing.
 * @param {Object} options - The options object.
 * @param {boolean|string} options.topSpacing - The top spacing value.
 * @param {boolean|string} options.forceTopSpacing - Whether to force top spacing.
 * @param {boolean|string} options.useTopSpacing - Whether to use top spacing.
 * @param {boolean|string} options.useTopSpacingOnHome - Whether to use top spacing on the home page.
 * @returns {boolean} A boolean value indicating if the component should show top spacing.
 */
export const checkShouldShowTopSpacing = ({
  topSpacing,
  forceTopSpacing,
  useTopSpacing,
  useTopSpacingOnHome,
}) => {
  const authorAddedTopSpacing = isTrue(forceTopSpacing) || isTrue(topSpacing)
  const authorRemovedTopSpacing =
    isFalse(forceTopSpacing) || isFalse(topSpacing)

  return (
    authorAddedTopSpacing ||
    (!authorRemovedTopSpacing &&
      (isTrue(useTopSpacing) || (isTrue(useTopSpacingOnHome) && isHomePage())))
  )
}

/**
 * Determines if the component should have a gray background and top spacing.
 * @param {Object} options - The options object.
 * @param {boolean} options.shouldShowTopSpacing - Whether the component should show top spacing.
 * @param {boolean} options.forceGrayBGForTopSpacing - Whether to force a gray background for top spacing.
 * @param {string} options.componentBGClassName - The component's background class name.
 * @returns {boolean} A boolean value indicating if the component should have a gray background and top spacing.
 */
export const checkShouldHaveTopSpacingContainer = ({
  shouldShowTopSpacing,
  forceGrayBGForTopSpacing,
  componentBGClassName,
}) =>
  (shouldShowTopSpacing === true && componentBGClassName === `wm-bg-gray`) ||
  (shouldShowTopSpacing && forceGrayBGForTopSpacing)

/**
 * Determines if the component should have a bottom spacing container.
 * @param {Object} options - The options object.
 * @param {boolean} options.shouldShowBottomSpacing - Whether the component should show bottom spacing.
 * @param {boolean} options.forceGrayBGForBottomSpacing - Whether to force a gray background for bottom spacing.
 * @param {string} options.componentBGClassName - The component's background class name.
 * @returns {boolean} A boolean value indicating if the component should have a bottom spacing container.
 */
export const checkShouldHaveBottomSpacingContainer = ({
  shouldShowBottomSpacing,
  forceGrayBGForBottomSpacing,
  componentBGClassName,
}) =>
  (shouldShowBottomSpacing === true && componentBGClassName === `wm-bg-gray`) ||
  (shouldShowBottomSpacing && forceGrayBGForBottomSpacing)

/**
 * Determines if the component should show bottom spacing.
 * @param {Object} options - The options object.
 * @param {boolean|string} options.bottomSpacing - The bottom spacing value.
 * @param {boolean|string} options.forceBottomSpacing - Whether to force bottom spacing.
 * @param {boolean|string} options.useBottomSpacing - Whether to use bottom spacing.
 * @param {boolean|string} options.useBottomSpacingOnHome - Whether to use bottom spacing on the home page.
 * @param {boolean|string} options.useBottomSpacingOnL1Page - Whether to use bottom spacing on L1 pages.
 * @returns {boolean} A boolean value indicating if the component should show bottom spacing.
 */
export const checkShouldShowBottomSpacing = ({
  bottomSpacing,
  forceBottomSpacing,
  useBottomSpacing,
  useBottomSpacingOnHome,
  useBottomSpacingOnL1Page,
}) => {
  /* If forceBottomSpacing is not specified, it'll be default, and we'll default to using it. */
  const authorAddedBottomSpacing =
    isTrue(forceBottomSpacing) || isTrue(bottomSpacing)
  /*
    We need to check if forceBottomSpacing is set exactly (===) to false because an undefined
    value does not mean we should force there to be no bottom spacing. Undefined means use the
    default setting for bottom spacing. Only a boolean value of false means to force there to
    be no bottom spacing even if the component itself specified for there to be bottom spacing..
  */
  const authorRemovedBottomSpacing =
    isFalse(forceBottomSpacing) || isFalse(bottomSpacing)

  // If author added bottom spacing then show it no matter what.
  return (
    authorAddedBottomSpacing ||
    // If author didn't hide bottom spacing then continue to see if we should show it:
    (!authorRemovedBottomSpacing &&
      // If component did not hide bottom spacing.
      !isFalse(useBottomSpacing) &&
      // If component did not hide bottom spacing for home page and we're on the home page.
      !(isFalse(useBottomSpacingOnHome) && isHomePage()) &&
      // If component did not hide bottom spacing for L1 pages and we're on an L1 page.
      !(isFalse(useBottomSpacingOnL1Page) && isL1Page()) &&
      // Show bottom sapcing by default.
      SHOULD_SHOW_BOTTOM_SPACING_BY_DEFAULT)
  )
}

/**
 * Determines the component's background class name.
 * @param {Object} options - The options object.
 * @param {boolean} options.forceGrayBGForComponent - Whether to force a gray background for the component.
 * @param {boolean} options.useGrayBG - Whether to use a gray background for the component.
 * @param {boolean} options.useGrayBGOnHome - Whether to use a gray background for the home page.
 * @param {boolean} options.useGrayBGOnL2Page - Whether to use a gray background for L2 pages.
 * @param {boolean} options.useGrayBGOnL3Page - Whether to use a gray background for L3 pages.
 * @param {string} options.componentBackgroundColor - The component's background color.
 * @param {boolean} options.useFloatingContainer - Whether to use a floating container.
 * @param {boolean} options.forceComponentBackgroundColor - Whether to force a background color for the component.
 * @returns {string} The component's background class name.
 */
export const getComponentBGClassName = ({
  forceGrayBGForComponent,
  useGrayBG,
  useGrayBGOnHome,
  useGrayBGOnL2Page,
  useGrayBGOnL3Page,
  componentBackgroundColor,
  useFloatingContainer,
  forceComponentBackgroundColor,
}) => {
  let componentBGClassName = ``
  if (isTrue(forceGrayBGForComponent)) {
    componentBGClassName = `wm-bg-gray`
  } else if (isFalse(forceGrayBGForComponent)) {
    componentBGClassName = `bg-white`
  } else if (
    (!useFloatingContainer || forceComponentBackgroundColor) &&
    componentBackgroundColor
  ) {
    componentBGClassName =
      componentBackgroundColor === `gray` || componentBackgroundColor === `sand`
        ? `wm-bg-gray`
        : componentBackgroundColor === `green`
        ? `wm-bg-green`
        : /*
            We don't have background color outside of container from negative margins floating
            containers to overlap adjacent components, so don't set default here to `white`.
          */
          ``
  } else if (
    useGrayBG ||
    (useGrayBGOnHome && isHomePage()) ||
    (useGrayBGOnL2Page && isL2Page()) ||
    (useGrayBGOnL3Page && isL3Page())
  ) {
    componentBGClassName = `wm-bg-gray`
  }

  return componentBGClassName
}

/**
 * Returns a new component that wraps the original component with spacing and background color.
 * @param {Object} [config] - An object that contains the default spacing settings for the component.
 * @param {boolean} [config.useTopSpacing] - Whether to use top spacing.
 * @param {boolean} [config.useTopSpacingOnHome] - Whether to use top spacing on the home page.
 * @param {boolean} [config.useBottomSpacing] - Whether to use bottom spacing.
 * @param {boolean} [config.useBottomSpacingOnL1Page] - Whether to use bottom spacing on the L1 page.
 * @param {boolean} [config.useBottomSpacingOnHome] - Whether to use bottom spacing on the home page.
 * @param {boolean} [config.useGrayBG] - Whether to use a gray background.
 * @param {boolean} [config.useGrayBGOnHome] - Whether to use a gray background on the home page.
 * @param {boolean} [config.useGrayBGOnL1Page] - Whether to use a gray background on the L1 page.
 * @param {boolean} [config.useGrayBGOnL2Page] - Whether to use a gray background on the L2 page.
 * @param {boolean} [config.useGrayBGOnL3Page] - Whether to use a gray background on the L3 page.
 * @param {boolean} [config.forceComponentBackgroundColor] - Whether to force the component background color.
 * @param {boolean} [config.useRebrandedColorPallete] - Whether to use the rebranded color palette.
 * @returns {Function} A function that takes a component as an argument and returns a new component.
 * @see
 * Config options:
 * - useTopSpacing
 * - useTopSpacingOnHome
 * - useBottomSpacing
 * - useBottomSpacingOnL1Page
 * - useBottomSpacingOnHome
 * - useGrayBG
 * - useGrayBGOnHome
 * - useGrayBGOnL1Page
 * - useGrayBGOnL2Page
 * - useGrayBGOnL3Page
 * - forceComponentBackgroundColor
 * - useRebrandedColorPallete
 */
export const withSpacing =
  (config = {}) =>
  (WrappedComponent) => {
    class WrappedComponentWithSpacing extends Component {
      constructor(props) {
        super(props)

        this.state = {
          shouldHide: false,
        }
      }

      get wrappedComponent() {
        const {
          forceTopSpacing,
          forceBottomSpacing,
          forceGrayBGForComponent,
          forceGrayBGForTopSpacing,
          forceGrayBGForBottomSpacing,
          useSpacingContainer,
          ...props
        } = this.props

        return (
          <WrappedComponent
            forceGrayBGForComponent={forceGrayBGForComponent}
            {...props}
            onShouldHide={(shouldHide) => {
              if (shouldHide !== this.state.shouldHide) {
                this.setState({
                  shouldHide,
                })
              }
            }}
          />
        )
      }

      scrollIntoComponent(divId) {
        divId.scrollIntoView({
          behavior: `smooth`,
          block: `start`,
          inline: `nearest`,
        })
      }

      componentDidMount() {
        /** Checking the url for #tags when the url changed
         * if the #tag is there in the url then will focus the respective component based on id
         */
        const { hash } = ssrWindow.location
        if (hash !== ``) {
          const id = hash.replace(`#`, ``)
          const element = ssrWindow.document.getElementById(id)
          if (element) {
            this.scrollIntoComponent(element)
          }
        }
        /* end url checking */
        /** Disabled reloading page and window close alert message */
        ssrWindow.onbeforeunload = null
      }

      render() {
        const {
          topSpacing,
          bottomSpacing,
          extraTopSpacing,
          extraBottomSpacing,
          topSpacingColor,
          bottomSpacingColor,
          forceTopSpacing,
          forceBottomSpacing,
          forceGrayBGForComponent,
          forceGrayBGForTopSpacing,
          forceGrayBGForBottomSpacing,
          useSpacingContainer,
          useExtraTopSpacing,
          useExtraBottomSpacing,
          layoutVariant,
          layoutVariantAlignment,
          componentId,
          cqType,
          componentBackgroundColor,
          useFloatingContainer,
          backgroundColor,
          targetId /** Adding targetId as data property for a component & is used by target team for executing activities based on that id */,
        } = this.props
        if (isFalse(useSpacingContainer)) {
          return this.wrappedComponent
        }

        let topSpacingClassNames = ``
        let bottomSpacingClassNames = ``

        /* Whether or not we should show top spacing. */
        const shouldShowTopSpacing = checkShouldShowTopSpacing({
          topSpacing,
          forceTopSpacing,
          useTopSpacing: config.useTopSpacing,
          useTopSpacingOnHome: config.useTopSpacingOnHome,
        })

        if (shouldShowTopSpacing) {
          /*
          useTopSpacingOnHome is currently just for the large contact module on the home page that
          specifically needs a pt-5 to look right. The ideal solution (when we have more time) is to
          fix up that component so that it doesn't have weird top spacing that forces us to do this.
        */
          if (isTrue(config.useTopSpacingOnHome) && isHomePage()) {
            topSpacingClassNames = `pt-5`
          } else {
            topSpacingClassNames = `topspacing`
          }
        }

        /* Whether or not we should show bottom spacing. */
        const shouldShowBottomSpacing = checkShouldShowBottomSpacing({
          bottomSpacing,
          forceBottomSpacing,
          useBottomSpacing: config.useBottomSpacing,
          useBottomSpacingOnHome: config.useBottomSpacingOnHome,
          useBottomSpacingOnL1Page: config.useBottomSpacingOnL1Page,
        })

        if (shouldShowBottomSpacing) {
          bottomSpacingClassNames = `bottomspacing`
        }

        /* Add background color if necessary. */
        const componentBGClassName = getComponentBGClassName({
          forceGrayBGForComponent,
          useGrayBG: config.useGrayBG,
          useGrayBGOnHome: config.useGrayBGOnHome,
          useGrayBGOnL2Page: config.useGrayBGOnL2Page,
          useGrayBGOnL3Page: config.useGrayBGOnL3Page,
          componentBackgroundColor,
          useFloatingContainer,
          forceComponentBackgroundColor: config.forceComponentBackgroundColor,
        })

        /* Note that !isTrue() is not the same as isFalse(). */
        const shouldHaveTopSpacingContainer =
          checkShouldHaveTopSpacingContainer({
            componentBGClassName,
            forceGrayBGForTopSpacing,
            shouldShowTopSpacing,
          })

        const shouldHaveBottomSpacingContainer =
          checkShouldHaveBottomSpacingContainer({
            componentBGClassName,
            forceGrayBGForBottomSpacing,
            shouldShowBottomSpacing,
          })

        let innermostClassNames = componentBGClassName
        let background = matchColors(backgroundColor) || ``
        const topBackgroundColor = matchColors(topSpacingColor) || ``
        const bottomBackgroundColor = matchColors(bottomSpacingColor) || ``

        if (
          !config.useRebrandedColorPallete &&
          !config.useRebrandedspacingColor
        ) {
          /* remove once all components are migrated over to 2.0 */
          background = backgroundColor
            ? backgroundColor === `white`
              ? `#fff`
              : backgroundColor === `gray` ||
                backgroundColor === `grey` ||
                backgroundColor === `sand`
              ? `#F4F4F4`
              : backgroundColor === `beige`
              ? `#EFEFE4`
              : backgroundColor === `green1`
              ? `#E7F8DF`
              : ``
            : ``
        }

        if (!shouldHaveTopSpacingContainer) {
          innermostClassNames = `${innermostClassNames}${
            innermostClassNames === `` ? `` : ` `
          }${topSpacingClassNames}`
        }

        if (!shouldHaveBottomSpacingContainer) {
          innermostClassNames = `${innermostClassNames}${
            innermostClassNames === `` ? `` : ` `
          }${bottomSpacingClassNames}`
        }
        const innermostClassNamesArr = trim(innermostClassNames).split(" ")
        const isBottomSpacingIncludes = innermostClassNamesArr.includes(
          bottomSpacingClassNames,
        )
        const isTopSpacingIncludes =
          innermostClassNamesArr.includes(topSpacingClassNames)

        /* Step 1: Add component background */
        let wrappedComponent =
          background !== topBackgroundColor ||
          background !== bottomBackgroundColor ? (
            <div
              data-target-id={targetId}
              style={{
                backgroundColor: background,
                position: `relative`,
              }}
            >
              {this.wrappedComponent}
            </div>
          ) : (
            this.wrappedComponent
          )

        /* Step 2: Add extra spacing */
        wrappedComponent =
          isBoolean(useExtraTopSpacing) ||
          isBoolean(useExtraBottomSpacing) ||
          isBoolean(extraTopSpacing) ||
          isBoolean(extraBottomSpacing) ? (
            <div
              className={`${
                useExtraTopSpacing || extraTopSpacing ? `pt-4 pt-lg-5` : ``
              } ${
                useExtraBottomSpacing || extraBottomSpacing
                  ? `pb-4 pb-lg-5`
                  : ``
              }`}
            >
              {wrappedComponent}
            </div>
          ) : (
            wrappedComponent
          )

        /* Step 3: Add component bottom spacing and corresponding background color */
        wrappedComponent = (
          <div
            data-target-id={targetId}
            style={{
              backgroundColor: bottomBackgroundColor,
              position: `relative`,
            }}
            className={
              this.state.shouldHide && isBottomSpacingIncludes
                ? ``
                : bottomSpacingClassNames
            }
          >
            {wrappedComponent}
          </div>
        )

        /* Step 4: Add component top spacing and corresponding background color */
        wrappedComponent = (
          <div
            data-target-id={targetId}
            style={{
              backgroundColor: topBackgroundColor,
              position: `relative`,
            }}
            className={
              this.state.shouldHide && isTopSpacingIncludes
                ? ``
                : topSpacingClassNames
            }
          >
            {wrappedComponent}
          </div>
        )

        /* Add another container for top spacing bg color if different from component bg color. */
        if (shouldHaveTopSpacingContainer) {
          let topSpacingClass = `wm-bg-gray`
          if (forceGrayBGForTopSpacing === `white`) {
            topSpacingClass = `bg-white`
          }

          wrappedComponent = (
            <div
              className={
                this.state.shouldHide
                  ? ``
                  : `${topSpacingClassNames} ${topSpacingClass}`
              }
            >
              {wrappedComponent}
            </div>
          )
        }

        /* Add another container for bottom spacing bg color if different from component bg color. */
        if (shouldHaveBottomSpacingContainer) {
          let bottomSpacingClass = `wm-bg-gray`
          if (forceGrayBGForBottomSpacing === `white`) {
            bottomSpacingClass = `bg-white`
          }

          wrappedComponent = (
            <div
              className={
                this.state.shouldHide
                  ? ``
                  : `${bottomSpacingClassNames} ${bottomSpacingClass}`
              }
            >
              {wrappedComponent}
            </div>
          )
        }

        /* Add another container for variant widths; Currently allowed: 1/2 width */
        if (layoutVariant !== `full`) {
          if (layoutVariantAlignment) {
            if (layoutVariant === `half`) {
              wrappedComponent = (
                <div className="half-width-wrapper">{wrappedComponent}</div>
              )
            } else if (layoutVariant === `oneThird`) {
              wrappedComponent = (
                <div className="one-third-width-wrapper">
                  {wrappedComponent}
                </div>
              )
            } else if (layoutVariant === `twoThird`) {
              wrappedComponent = (
                <div className="two-third-width-wrapper">
                  {wrappedComponent}
                </div>
              )
            }
            wrappedComponent = (
              <div className={`${layoutVariantAlignment}-alignment-wrapper`}>
                {wrappedComponent}
              </div>
            )
          }
        }

        /* tabindex added for accessibility when anchoring to wrapped components, this allows us to shift focus and maintain the page's current tabindex */
        if (cqType.includes(`singlecolumntext`)) {
          wrappedComponent = (
            <div data-attribute={componentId} tabIndex="-1">
              {wrappedComponent}
            </div>
          )
        } else {
          wrappedComponent = (
            <div id={componentId} tabIndex="-1">
              {wrappedComponent}
            </div>
          )
        }

        /*
          Do not return null when shouldHide is false, because the component itself will unmount
          in that case, and we need it to stay mounted to be able to update shouldHide if it needs
          to be displayed again after an update within the component.
        */
        return wrappedComponent
      }
    }

    /*
      AEM dev team may accidentally pass these boolean values as string, so we'll accommodate both
      for now.
    */
    WrappedComponentWithSpacing.propTypes = {
      /*
        Setting to true (boolean) will override default spacing and force top spacing for the component.
        Setting to false will override default spacing and force there to be no top spacing.
        Leaving as undefined means using default bottom spacing for the component.
      */
      forceTopSpacing: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
      /*
        Setting to true will override default spacing and force bottom spacing for the component.
        Setting to false will override default spacing and force there to be no bottom spacing.
        Leaving as undefined means using default bottom spacing for the component.
      */
      forceBottomSpacing: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.bool,
      ]),
      /*
        Setting to true will override default background color for the component and force it to be gray.
        Setting to false will override default background color and force it to be white.
        Leaving as undefined means the default background color for the component will be used.
      */
      forceGrayBGForComponent: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.bool,
      ]),
      /*
        Setting to true will override default top spacing background color and force it to be gray.
        Setting to false will override default top spacing background color and force it to be white.
        Leaving as undefined means the default background color for the top spacing will be used.
      */
      forceGrayBGForTopSpacing: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.bool,
      ]),
      /*
        Setting to true will override default bottom spacing background color and force it to be gray.
        Setting to false will override default bottom spacing background color and force it to be white.
        Leaving as undefined means the default background color for the bottom spacing will be used.
      */
      forceGrayBGForBottomSpacing: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.bool,
      ]),
      /* Whether or not the component is authored as a variant-width component */
      layoutVariant: PropTypes.string,
      /* Alignment of the variant-width component */
      layoutVariantAlignment: PropTypes.string,
      /*
        This is not passed by AEM, but by our own React app.
        If set to true, we don't use a spacing container.
      */
      useSpacingContainer: PropTypes.bool,
      componentId: PropTypes.string,
      cqType: PropTypes.string,
      useExtraTopSpacing: PropTypes.bool,
      useExtraBottomSpacing: PropTypes.bool,
      /* Background color for components, but kept within a container. */
      componentBackgroundColor: PropTypes.string,
      /* Whether or not the component has been wrapped with a floating card container. */
      useFloatingContainer: PropTypes.bool,
      targetId: PropTypes.string,
      backgroundColor: PropTypes.string,
      topSpacing: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
      bottomSpacing: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
      extraTopSpacing: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
      extraBottomSpacing: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.bool,
      ]),
      topSpacingColor: PropTypes.string,
      bottomSpacingColor: PropTypes.string,
    }

    WrappedComponentWithSpacing.defaultProps = {
      componentId: "",
      cqType: "",
      targetId: "",
      useSpacingContainer: true,
      backgroundColor: "",
      componentBackgroundColor: ``,
      useFloatingContainer: false,
      layoutVariant: `full`,
      layoutVariantAlignment: `left`,
      forceTopSpacing: "",
      forceBottomSpacing: "",
      forceGrayBGForComponent: null,
      forceGrayBGForTopSpacing: null,
      forceGrayBGForBottomSpacing: null,
      useExtraTopSpacing: false,
      useExtraBottomSpacing: false,
      topSpacing: ``,
      bottomSpacing: ``,
      extraTopSpacing: ``,
      extraBottomSpacing: ``,
      topSpacingColor: ``,
      bottomSpacingColor: ``,
    }

    return WrappedComponentWithSpacing
  }
